import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { rupiah, rupiahShort } from "@/lib/format";
import FilterBar from "@/components/FilterBar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingUp, TriangleAlert, Radio, Layers, Download, Clock3, ChevronRight, ChevronDown } from "lucide-react";
import LeadDetailSheet from "@/components/LeadDetailSheet";
import { fmtDate } from "@/lib/format";

const ts = { background: "#18181B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 };
// Recharts does not read contentStyle for the label/item text color — without these,
// tooltip text silently inherits the page's default (near-black in light mode),
// landing dark-on-dark against the tooltip's own dark background.
const tsItem = { color: "#e5e7eb" };
const tsLabel = { color: "#93c5fd" };
const COLORS = ["#3B82F6", "#22D3EE", "#F59E0B", "#10B981", "#EF4444", "#6366F1", "#EC4899", "#14B8A6"];

const LOST_SOLUTIONS = {
  "harga": "Tawarkan paket bertahap / cicilan & tonjolkan value (garansi, after-sales).",
  "mahal": "Buat opsi budget + upsell bertahap; bandingkan value vs kompetitor.",
  "kompetitor": "Perkuat diferensiasi (portfolio, garansi, timeline) & percepat follow-up.",
  "budget": "Siapkan paket ekonomis + skema pembayaran fleksibel.",
  "lambat": "Kirim SPH < 24 jam & aktifkan reminder follow-up otomatis.",
  "respon": "Aktifkan template WA cepat & jadwal follow-up terstruktur.",
  "jadi": "Nurturing berkala (broadcast promo) & tanyakan kebutuhan ulang.",
  "jauh": "Tawarkan survey/konsultasi online agar jarak bukan hambatan.",
  "default": "Analisa kebutuhan lebih dalam & jadwalkan follow-up terstruktur.",
};
const suggestSolution = (name) => {
  const n = (name || "").toLowerCase();
  for (const k of Object.keys(LOST_SOLUTIONS)) if (k !== "default" && n.includes(k)) return LOST_SOLUTIONS[k];
  return LOST_SOLUTIONS.default;
};

function ChannelCard({ c }) {
  return (
    <div className="card-surface rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Radio size={16} className="text-blue-400" /><span className="font-semibold">{c.name}</span></div>
        <span className="text-xs px-2 py-0.5 rounded bg-blue-500/15 text-blue-300">{c.contribution_pct}% kontribusi</span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center mb-3">
        <div><div className="text-lg font-bold font-heading">{c.total}</div><div className="text-[10px] text-zinc-500 uppercase">Leads</div></div>
        <div><div className="text-lg font-bold font-heading text-emerald-400">{c.closing}</div><div className="text-[10px] text-zinc-500 uppercase">Closing</div></div>
        <div><div className="text-lg font-bold font-heading text-cyan-400">{c.closing_rate}%</div><div className="text-[10px] text-zinc-500 uppercase">Rate</div></div>
        <div><div className="text-lg font-bold font-heading text-red-400">{c.lost}</div><div className="text-[10px] text-zinc-500 uppercase">Lost</div></div>
      </div>
      <div className="text-sm mb-3"><span className="text-zinc-500">Nilai closing: </span><span className="font-semibold text-emerald-400">{rupiah(c.value)}</span></div>
      <div className="border-t border-white/5 pt-2">
        <div className="text-[10px] text-zinc-500 uppercase mb-1.5">Kelompok Umur Customer</div>
        <div className="flex flex-wrap gap-1.5">
          {c.age_groups.map((a) => <span key={a.name} className="text-xs px-2 py-0.5 rounded-full bg-[#18181B] border border-white/10">{a.name}: <b>{a.value}</b></span>)}
        </div>
      </div>
    </div>
  );
}

function KategoriCard({ k }) {
  return (
    <div className="card-surface rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Layers size={16} className="text-cyan-400" /><span className="font-semibold">{k.name}</span></div>
        <span className="text-xs px-2 py-0.5 rounded bg-cyan-500/15 text-cyan-300">{k.closing_rate}% closing</span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center mb-3">
        <div><div className="text-lg font-bold font-heading">{k.total}</div><div className="text-[10px] text-zinc-500 uppercase">Leads</div></div>
        <div><div className="text-lg font-bold font-heading text-emerald-400">{k.closing}</div><div className="text-[10px] text-zinc-500 uppercase">Closing</div></div>
        <div><div className="text-sm font-bold font-heading text-emerald-400 mt-1">{rupiahShort(k.value)}</div><div className="text-[10px] text-zinc-500 uppercase">Nilai</div></div>
      </div>
      <div className="border-t border-white/5 pt-2">
        <div className="text-[10px] text-zinc-500 uppercase mb-1.5">Layanan/Produk Terpopuler</div>
        {k.top_layanan.length === 0 ? <span className="text-xs text-zinc-600">-</span> : (
          <div className="space-y-1">
            {k.top_layanan.map((l, i) => (
              <div key={l.name} className="flex items-center justify-between text-xs">
                <span className="text-zinc-300">{i + 1}. {l.name}</span><span className="text-zinc-500">{l.value}x</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// "Durasi Pipeline" — berapa lama tiap client dari masuk sampai closing/lost, dan di
// tahap mana waktunya paling banyak habis (data direkonstruksi backend dari audit trail
// yang sudah ada, lihat _lead_stage_segments di server.py). Klik baris untuk buka detail
// lead, klik chevron untuk expand rincian per-tahap tanpa pindah halaman.
function DurasiPipelineTab({ flow }) {
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(null);
  if (!flow) return null;
  const items = flow.items || [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="card-surface rounded-2xl p-5">
          <div className="flex items-center gap-2 text-emerald-400 mb-1"><Clock3 size={18} /><span className="font-semibold text-sm">Rata-rata sampai Closing</span></div>
          <div className="text-2xl font-bold font-heading tabular-nums">{flow.avg_closing_workdays} <span className="text-sm font-normal text-zinc-500">hari kerja</span></div>
        </div>
        <div className="card-surface rounded-2xl p-5">
          <div className="flex items-center gap-2 text-red-400 mb-1"><Clock3 size={18} /><span className="font-semibold text-sm">Rata-rata sampai Lost</span></div>
          <div className="text-2xl font-bold font-heading tabular-nums">{flow.avg_lost_workdays} <span className="text-sm font-normal text-zinc-500">hari kerja</span></div>
        </div>
      </div>

      <div className="card-surface rounded-2xl overflow-hidden" data-testid="durasi-table">
        <div className="p-4 pb-2">
          <h3 className="font-heading font-semibold text-sm uppercase tracking-wide text-zinc-400">Riwayat Durasi per Client</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Dari tanggal masuk sampai closing/lost — klik baris untuk buka client, klik panah untuk lihat rincian per tahap.</p>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-zinc-600 text-center py-8">Belum ada client yang closing/lost.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((it) => {
              const isOpen = expanded === it.lead_id;
              return (
                <div key={it.lead_id}>
                  <div data-testid={`durasi-row-${it.lead_id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] cursor-pointer"
                    onClick={() => setSelected(it.lead_id)}>
                    <button data-testid={`durasi-expand-${it.lead_id}`} onClick={(e) => { e.stopPropagation(); setExpanded(isOpen ? null : it.lead_id); }}
                      className="shrink-0 p-1 rounded hover:bg-white/10 text-zinc-500">
                      {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{it.nama}</div>
                      <div className="text-[11px] text-zinc-500">{it.sales_name} · {it.branch_name} · Masuk {fmtDate(it.created_at)} → {fmtDate(it.ended_at)}</div>
                      {it.final_status === "Lost" && it.alasan_lost && <div className="text-[11px] text-red-400 mt-0.5">Alasan: {it.alasan_lost}</div>}
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${it.final_status === "Closing" ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>{it.final_status}</span>
                    <span className="text-sm font-bold tabular-nums shrink-0 w-20 text-right">{it.total_workdays}h</span>
                  </div>
                  {isOpen && (
                    <div className="px-4 pb-3 pl-11 space-y-1.5" data-testid={`durasi-segments-${it.lead_id}`}>
                      {(it.segments || []).length === 0 ? (
                        <p className="text-xs text-zinc-600">Tidak ada riwayat perpindahan tahap tercatat.</p>
                      ) : it.segments.map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-xs bg-[#18181B] rounded-lg px-3 py-2">
                          <span className="font-medium">{s.stage}</span>
                          <span className="text-zinc-500">{fmtDate(s.entered_at)} → {fmtDate(s.exited_at)}</span>
                          <span className="tabular-nums text-zinc-300 shrink-0 ml-2">{s.workdays} hari kerja</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <LeadDetailSheet leadId={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

export default function Insights() {
  const [qs, setQs] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["insights", qs], queryFn: async () => (await api.get(`/insights?${qs}`)).data });
  const { data: flow } = useQuery({ queryKey: ["pipeline-flow"], queryFn: async () => (await api.get("/reports/pipeline-flow")).data });

  return (
    <div className="px-4 py-5 max-w-5xl mx-auto print-area md:max-w-none md:px-8">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h1 className="text-xl font-bold font-heading tracking-tight">Insight & Analisis</h1>
          <p className="text-sm text-zinc-500 mb-1">Dari mana leads masuk, segmen customer, dan akar masalah lost order</p>
        </div>
        <button data-testid="print-insight" onClick={() => window.print()} className="no-print shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#18181B] border border-white/10 text-sm text-zinc-300 hover:text-white">
          <Download size={15} />Unduh PDF
        </button>
      </div>
      <div className="no-print"><FilterBar onChange={setQs} /></div>

      {isLoading || !data ? null : (
        <Tabs defaultValue="channel">
          <TabsList className="bg-[#18181B] w-full">
            <TabsTrigger value="channel" className="flex-1">Channel</TabsTrigger>
            <TabsTrigger value="kategori" className="flex-1">Kategori</TabsTrigger>
            <TabsTrigger value="lost" className="flex-1">Lost Order</TabsTrigger>
            <TabsTrigger value="durasi" className="flex-1" data-testid="tab-durasi">Durasi Pipeline</TabsTrigger>
          </TabsList>

          <TabsContent value="channel" className="mt-4 space-y-4">
            <div className="card-surface rounded-2xl p-5">
              <h3 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wide text-zinc-400">Performa per Channel</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.channel}>
                  <XAxis dataKey="name" tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={ts} itemStyle={tsItem} labelStyle={tsLabel} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="total" name="Leads" fill="#6366F1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="closing" name="Closing" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="lost" name="Lost" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {data.channel.map((c) => <ChannelCard key={c.name} c={c} />)}
            </div>
          </TabsContent>

          <TabsContent value="kategori" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {data.kategori.map((k) => <KategoriCard key={k.name} k={k} />)}
              {data.kategori.length === 0 && <p className="text-zinc-600 text-sm">Tidak ada data</p>}
            </div>
          </TabsContent>

          <TabsContent value="lost" className="mt-4 space-y-4">
            <div className="card-surface rounded-2xl p-5">
              <div className="flex items-center gap-2 text-red-400 mb-1"><TriangleAlert size={18} /><span className="font-semibold">Lost Revenue</span></div>
              <div className="font-bold font-heading text-red-400 whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: "clamp(1rem, 3.5vw, 1.5rem)", fontVariantNumeric: "tabular-nums" }} title={rupiah(data.lost.total_lost_value)}>{rupiah(data.lost.total_lost_value)}</div>
              <div className="text-xs text-zinc-500">{data.lost.total_lost} order hilang</div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="card-surface rounded-2xl p-5">
                <h3 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wide text-zinc-400">Alasan Lost — Breakdown</h3>
                {data.lost.reasons.length === 0 ? <p className="text-sm text-zinc-600">Tidak ada</p> : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={data.lost.reasons} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                        {data.lost.reasons.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={ts} itemStyle={tsItem} labelStyle={tsLabel} formatter={(v) => rupiah(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="card-surface rounded-2xl p-5">
                <h3 className="font-heading font-semibold mb-4 text-sm uppercase tracking-wide text-zinc-400">Lost per Channel / Sumber</h3>
                <div className="space-y-2">
                  {data.lost.per_channel.map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-[#18181B]">
                      <span className="text-sm font-medium">{c.name}</span>
                      <div className="text-right"><div className="text-sm text-red-400 font-semibold">{rupiah(c.value)}</div><div className="text-[11px] text-zinc-500">{c.count} lost</div></div>
                    </div>
                  ))}
                  {data.lost.per_channel.length === 0 && <p className="text-sm text-zinc-600">Tidak ada</p>}
                </div>
              </div>
            </div>

            <div className="card-surface rounded-2xl p-5" data-testid="lost-detail-table">
              <h3 className="font-heading font-semibold mb-1 text-sm uppercase tracking-wide text-zinc-400">Detail Alasan Lost + Saran Solusi</h3>
              <p className="text-xs text-zinc-500 mb-3">Estimasi potensi omset yang hilang & rekomendasi tindakan per alasan.</p>
              {data.lost.reasons.length === 0 ? <p className="text-sm text-zinc-600">Belum ada order lost.</p> : (
                <div className="overflow-x-auto hide-scrollbar">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead><tr className="text-zinc-500 text-xs uppercase border-b border-white/10">
                      <th className="text-left py-2.5 px-3 font-medium">Alasan</th>
                      <th className="text-right font-medium">Jumlah</th>
                      <th className="text-right font-medium">Potensi Omset Hilang</th>
                      <th className="text-right font-medium">% Total</th>
                      <th className="text-left font-medium px-3">Saran Solusi</th>
                    </tr></thead>
                    <tbody>
                      {data.lost.reasons.map((r, i) => {
                        const pct = data.lost.total_lost_value ? Math.round((r.value / data.lost.total_lost_value) * 100) : 0;
                        return (
                          <tr key={i} data-testid={`lost-reason-${i}`} className="border-b border-white/5 last:border-0">
                            <td className="py-2.5 px-3 font-medium whitespace-nowrap">{r.name}</td>
                            <td className="text-right text-zinc-400">{r.count}</td>
                            <td className="text-right font-semibold text-red-400 whitespace-nowrap tabular-nums">{rupiah(r.value)}</td>
                            <td className="text-right text-zinc-400">{pct}%</td>
                            <td className="px-3 text-xs text-zinc-300 min-w-[240px]">{suggestSolution(r.name)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="durasi" className="mt-4 space-y-4">
            <DurasiPipelineTab flow={flow} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
